/**
 * The code ↔ address list a tenant downloads to send a promo-code campaign
 * (#2249). Pure and import-free apart from the cell escaping, so it is testable.
 *
 * Built client-side from JSON the backend returns, the same way the bookings
 * export is — the backend never produces a file.
 */

import { escapeCSVField } from './csvFieldEscape';
import { stationCalendarDay } from './stationTime';

export interface CampaignCsvRow {
    code: string | null;
    recipientEmail: string | null;
    redeemed: boolean;
    bookingNumbers: string[];
}

export interface CampaignCsvLabels {
    code: string;
    email: string;
    redeemed: string;
    bookings: string;
    yes: string;
    no: string;
    /** For a cell with no value — an erased address, a code not yet redeemed. */
    missing: string;
}

export const buildCampaignCsv = (rows: CampaignCsvRow[], labels: CampaignCsvLabels): string => {
    const header = [labels.code, labels.email, labels.redeemed, labels.bookings];
    const lines = rows.map((row) => [
        row.code ?? labels.missing,
        row.recipientEmail ?? labels.missing,
        row.redeemed ? labels.yes : labels.no,
        row.bookingNumbers.length > 0 ? row.bookingNumbers.join(', ') : labels.missing,
    ]);
    return [header, ...lines]
        .map((cells) => cells.map(escapeCSVField).join(';'))
        .join('\n');
};

/** `vonovia-summer-2026-codes-2026-09-15.csv` — readable, and safe on every OS. */
export const campaignCsvFileName = (
    campaignName: string,
    label: string,
    today: Date = new Date(),
): string => {
    // Split and re-join instead of trimming hyphens with an anchored regex, which
    // backtracks on a long run of separators.
    const slug = campaignName
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .split(/[^a-z0-9]/)
        .filter((part) => part.length > 0)
        .join('-');
    // The station day (ADR-0019) — the UTC date named a file exported at
    // 00:30 in Berlin after the previous day.
    const date = stationCalendarDay(today);
    return `${slug || 'campaign'}-${label}-${date}.csv`;
};
